import * as i0 from '@angular/core';
import { ViewContainerRef, Component, Inject, ViewChild, HostListener, EventEmitter, Input, Output, NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import * as i2 from '@angular/material/dialog';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import * as i3 from '@sick-curie/dashboard-builder-core';
import { DashboardBuilderCoreModule } from '@sick-curie/dashboard-builder-core';
import { DashboardDisplayType, DashboardNewTilePosition, DEFAULT_PALLETE, createDefaultDashboardSettings } from '@sick-curie/dashboard-builder-models';
import * as i1 from '@sick-curie/dashboard-builder-services';
import { DashboardStructureService, DashboardManagementService } from '@sick-curie/dashboard-builder-services';
import { HelpSystemEvent, dashboardElements } from '@sick-curie/dashboard-builder-ui-models';
import * as i5 from '@sick-curie/dashboard-builder-view';
import { ConfigureElementDialogComponent, ElementIssueDialogComponent, AddElementDialogComponent, DashboardGridComponent, DashboardBuilderViewModule } from '@sick-curie/dashboard-builder-view';
import * as _ from 'lodash-es';
import { cloneDeep, merge } from 'lodash-es';
import { Subject, combineLatest, fromEvent, takeUntil } from 'rxjs';
import * as i4 from '@angular/common';
import { CommonModule } from '@angular/common';

var ModalDialogComponentButton;
(function (ModalDialogComponentButton) {
    ModalDialogComponentButton["Cancel"] = "Cancel";
    ModalDialogComponentButton["Close"] = "Close";
    ModalDialogComponentButton["Save"] = "Save";
})(ModalDialogComponentButton || (ModalDialogComponentButton = {}));
class ModalDialogComponent {
    constructor(element, componentFactoryResolver, notificationService, dialogRef, data) {
        this.element = element;
        this.componentFactoryResolver = componentFactoryResolver;
        this.notificationService = notificationService;
        this.dialogRef = dialogRef;
        this.actionIcon = {
            icon: 'action/help',
            title: 'Toggle mouse over help',
            fill: undefined,
            action: this.toggleShowHelp.bind(this),
            isHelp: true,
        };
        this.closeOnAdd = true;
        this.title = data.title;
        this.titleBadge = data.titleBadge;
        this.component = data.component;
        this.componentData = data.componentData;
        this.showReset = data.showReset;
        this.showClose = data.showClose;
        this.showCloseOnAdd = data.showCloseOnAdd;
        this.showSave = data.showSave;
        this.saveText = data.saveText || 'Save';
        this.showCancel = data.showCancel;
        this.actionIcon = data.actionItem ? data.actionItem : this.actionIcon;
        this.smallHeader = data.smallHeader;
        this.showHelp = false;
    }
    onKeyUp() {
        this.cancel();
    }
    ngOnInit() {
        this.loadComponent();
    }
    ngOnDestroy() {
        if (this.componentRef) {
            this.componentRef.destroy();
        }
    }
    loadComponent() {
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory(this.component);
        this.componentRef = this.dialogComponent.createComponent(componentFactory);
        const instance = this.componentRef.instance;
        instance.data = this.componentData;
        instance.result.subscribe((data) => this.result(data));
        instance.notify?.subscribe((data) => this.notify(data));
        instance.save?.subscribe(() => this.executeSave());
        instance.cancel?.subscribe(() => this.executeCancel());
        instance.saveDisabled?.subscribe((disableSave) => {
            setTimeout(() => (this.disableSave = disableSave));
        });
        this.componentDataReset = _.cloneDeep(this.componentData);
    }
    result(data) {
        this.componentResult = data;
        if (this.showCloseOnAdd && this.closeOnAdd) {
            this.close();
        }
    }
    notify(data) {
        this.notificationService.show(data);
    }
    reset() {
        const componentInstance = this.componentRef.instance;
        if (typeof componentInstance.reset === 'function') {
            componentInstance.data = _.cloneDeep(this.componentDataReset);
            componentInstance.reset();
        }
        else {
            // eslint-disable-next-line no-console
            console.error('Can not execute reset');
        }
    }
    close() {
        this.dialogRef.close({ button: ModalDialogComponentButton.Close, data: this.componentResult });
    }
    save() {
        let saveAndExit = true;
        if (this.componentRef.instance.saveAction) {
            saveAndExit = this.componentRef.instance.saveAction();
        }
        if (saveAndExit) {
            this.executeSave();
        }
    }
    cancel() {
        let cancelAndExit = true;
        if (this.componentRef.instance.cancelAction) {
            cancelAndExit = this.componentRef.instance.cancelAction();
        }
        if (cancelAndExit) {
            this.executeCancel();
        }
    }
    toggleShowHelp() {
        this.showHelp = !this.showHelp;
        this.element.nativeElement.dispatchEvent(new HelpSystemEvent(this.showHelp));
    }
    get showButtons() {
        return this.showCancel || this.showSave || this.showClose || this.showCloseOnAdd || this.showReset;
    }
    executeSave() {
        this.dialogRef.close({ button: ModalDialogComponentButton.Save, data: this.componentResult });
    }
    executeCancel() {
        this.dialogRef.close({ button: ModalDialogComponentButton.Cancel });
    }
}
ModalDialogComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: ModalDialogComponent, deps: [{ token: i0.ElementRef }, { token: i0.ComponentFactoryResolver }, { token: i3.NotificationService }, { token: i2.MatDialogRef }, { token: MAT_DIALOG_DATA }], target: i0.ɵɵFactoryTarget.Component });
ModalDialogComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.0.0", type: ModalDialogComponent, selector: "dashboard-builder-modal-dialog", host: { listeners: { "window:keyup.esc": "onKeyUp()" } }, viewQueries: [{ propertyName: "dialogComponent", first: true, predicate: ["dialogComponent"], descendants: true, read: ViewContainerRef, static: true }], ngImport: i0, template: "<div class=\"dialog-title\" [class.small-header]=\"smallHeader\">\n  <h5 mat-dialog-title>\n    {{ title }}\n    <dashboard-builder-badge [type]=\"titleBadge.type\" *ngIf=\"titleBadge\">{{ titleBadge.text }}</dashboard-builder-badge>\n  </h5>\n  <div *ngIf=\"actionIcon.action; then action; else noaction\"></div>\n  <ng-template #action>\n    <davinci-button\n      [icon]=\"actionIcon.icon\"\n      [title]=\"actionIcon.title\"\n      class=\"error\"\n      [class.dashboard-builder-modal-dialog-help]=\"actionIcon.isHelp\"\n      [style]=\"actionIcon.fill ? { fill: actionIcon.fill } : {}\"\n      type=\"bare\"\n      (click)=\"actionIcon.action ? actionIcon.action() : null\"\n    ></davinci-button\n  ></ng-template>\n  <ng-template #noaction>\n    <davinci-icon\n      disabled\n      [icon]=\"actionIcon.icon\"\n      [title]=\"actionIcon.title\"\n      [style]=\"actionIcon.fill ? { fill: actionIcon.fill } : {}\"\n    ></davinci-icon>\n  </ng-template>\n</div>\n<div class=\"dialog-content\">\n  <div #dialogComponent></div>\n</div>\n<mat-dialog-actions *ngIf=\"showCloseOnAdd || showReset || showCancel || showSave || showClose\">\n  <davinci-checkbox *ngIf=\"showCloseOnAdd\" (change)=\"closeOnAdd = !closeOnAdd\" [(checked)]=\"closeOnAdd\">\n    Close dialog on add\n  </davinci-checkbox>\n  <davinci-button *ngIf=\"showReset\" (click)=\"reset()\" type=\"outline\">Use defaults</davinci-button>\n  <div class=\"dbb-fill\"></div>\n  <davinci-button *ngIf=\"showCancel\" (click)=\"cancel()\" type=\"outline\">Cancel</davinci-button>\n  <davinci-button *ngIf=\"showSave\" [disabled]=\"disableSave\" (click)=\"save()\">{{ saveText }}</davinci-button>\n  <davinci-button *ngIf=\"showClose\" (click)=\"close()\" type=\"outline\">Close</davinci-button>\n</mat-dialog-actions>\n", styles: [":host{color:var(--davinci-color-text,#2C3133);background-color:var(--davinci-color-background-01dp, #ffffff)!important}mat-dialog-container{border-radius:0}.mat-dialog-title{font-family:Open Sans,OpenSans,sans-serif;font-size:1.2rem;font-weight:700}.dialog-content{background-color:var(--davinci-color-background-01dp, #ffffff)!important}.dialog-title{margin:0 0 24px;display:flex;max-height:1.68rem;align-items:center}.dialog-title h5{margin:0;flex-grow:1}.dialog-title .dashboard-builder-modal-dialog-help{display:none}.dialog-title .dashboard-builder-modal-dialog-help.is-visible{display:block}.dialog-title.small-header davinci-button{min-width:1.2rem;min-height:1.2rem;width:1.2rem;height:1.2rem}.dialog-title dashboard-builder-badge{margin-left:6px;text-transform:uppercase}.dialog-title davinci-button{margin:0;min-width:1.8rem;min-height:1.8rem;width:1.8rem;height:1.8rem}mat-dialog-actions{margin:48px 0 0;padding:0;display:flex;flex-direction:row;align-items:center}mat-dialog-actions davinci-button{min-width:80px;margin:0 24px 0 0}mat-dialog-actions davinci-button:last-child{margin-right:0}\n"], dependencies: [{ kind: "directive", type: i4.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i3.BadgeComponent, selector: "dashboard-builder-badge", inputs: ["type", "round", "center"] }, { kind: "directive", type: i2.MatDialogTitle, selector: "[mat-dialog-title], [matDialogTitle]", inputs: ["id"], exportAs: ["matDialogTitle"] }, { kind: "directive", type: i2.MatDialogActions, selector: "[mat-dialog-actions], mat-dialog-actions, [matDialogActions]", inputs: ["align"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: ModalDialogComponent, decorators: [{
            type: Component,
            args: [{ selector: 'dashboard-builder-modal-dialog', template: "<div class=\"dialog-title\" [class.small-header]=\"smallHeader\">\n  <h5 mat-dialog-title>\n    {{ title }}\n    <dashboard-builder-badge [type]=\"titleBadge.type\" *ngIf=\"titleBadge\">{{ titleBadge.text }}</dashboard-builder-badge>\n  </h5>\n  <div *ngIf=\"actionIcon.action; then action; else noaction\"></div>\n  <ng-template #action>\n    <davinci-button\n      [icon]=\"actionIcon.icon\"\n      [title]=\"actionIcon.title\"\n      class=\"error\"\n      [class.dashboard-builder-modal-dialog-help]=\"actionIcon.isHelp\"\n      [style]=\"actionIcon.fill ? { fill: actionIcon.fill } : {}\"\n      type=\"bare\"\n      (click)=\"actionIcon.action ? actionIcon.action() : null\"\n    ></davinci-button\n  ></ng-template>\n  <ng-template #noaction>\n    <davinci-icon\n      disabled\n      [icon]=\"actionIcon.icon\"\n      [title]=\"actionIcon.title\"\n      [style]=\"actionIcon.fill ? { fill: actionIcon.fill } : {}\"\n    ></davinci-icon>\n  </ng-template>\n</div>\n<div class=\"dialog-content\">\n  <div #dialogComponent></div>\n</div>\n<mat-dialog-actions *ngIf=\"showCloseOnAdd || showReset || showCancel || showSave || showClose\">\n  <davinci-checkbox *ngIf=\"showCloseOnAdd\" (change)=\"closeOnAdd = !closeOnAdd\" [(checked)]=\"closeOnAdd\">\n    Close dialog on add\n  </davinci-checkbox>\n  <davinci-button *ngIf=\"showReset\" (click)=\"reset()\" type=\"outline\">Use defaults</davinci-button>\n  <div class=\"dbb-fill\"></div>\n  <davinci-button *ngIf=\"showCancel\" (click)=\"cancel()\" type=\"outline\">Cancel</davinci-button>\n  <davinci-button *ngIf=\"showSave\" [disabled]=\"disableSave\" (click)=\"save()\">{{ saveText }}</davinci-button>\n  <davinci-button *ngIf=\"showClose\" (click)=\"close()\" type=\"outline\">Close</davinci-button>\n</mat-dialog-actions>\n", styles: [":host{color:var(--davinci-color-text,#2C3133);background-color:var(--davinci-color-background-01dp, #ffffff)!important}mat-dialog-container{border-radius:0}.mat-dialog-title{font-family:Open Sans,OpenSans,sans-serif;font-size:1.2rem;font-weight:700}.dialog-content{background-color:var(--davinci-color-background-01dp, #ffffff)!important}.dialog-title{margin:0 0 24px;display:flex;max-height:1.68rem;align-items:center}.dialog-title h5{margin:0;flex-grow:1}.dialog-title .dashboard-builder-modal-dialog-help{display:none}.dialog-title .dashboard-builder-modal-dialog-help.is-visible{display:block}.dialog-title.small-header davinci-button{min-width:1.2rem;min-height:1.2rem;width:1.2rem;height:1.2rem}.dialog-title dashboard-builder-badge{margin-left:6px;text-transform:uppercase}.dialog-title davinci-button{margin:0;min-width:1.8rem;min-height:1.8rem;width:1.8rem;height:1.8rem}mat-dialog-actions{margin:48px 0 0;padding:0;display:flex;flex-direction:row;align-items:center}mat-dialog-actions davinci-button{min-width:80px;margin:0 24px 0 0}mat-dialog-actions davinci-button:last-child{margin-right:0}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.ComponentFactoryResolver }, { type: i3.NotificationService }, { type: i2.MatDialogRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_DIALOG_DATA]
                }] }]; }, propDecorators: { dialogComponent: [{
                type: ViewChild,
                args: ['dialogComponent', { read: ViewContainerRef, static: true }]
            }], onKeyUp: [{
                type: HostListener,
                args: ['window:keyup.esc']
            }] } });

/**
 * @copyright
 * Copyright(c) 2021 SICK AG
 */
class DefaultDashboardComponent {
    constructor(structureService, managementService, dialog, elementDefinitionService, notificationService) {
        this.structureService = structureService;
        this.managementService = managementService;
        this.dialog = dialog;
        this.elementDefinitionService = elementDefinitionService;
        this.notificationService = notificationService;
        this.displayType = {
            fixed: DashboardDisplayType.Fixed,
            mobile: DashboardDisplayType.Mobile,
            auto: DashboardDisplayType.Auto,
        };
        this.rootNode = cloneDeep(DashboardStructureService.EMPTY);
        this.settings = cloneDeep(DefaultDashboardComponent.DEFAULT_SETTINGS);
        this.editMode = false;
        this.multiselect = false;
        this.rootNodeChange = new EventEmitter();
        this.insertModeChange = new EventEmitter();
        this.gridNodesChange = new EventEmitter();
        this.elementIssues = [];
        this.stopInsertingNode$ = new Subject();
        this.setRootNode(this.rootNode);
        this.structureService.structureChanged.subscribe(() => this.rootNodeChange.emit(this.structureService.getRootNode()));
        combineLatest([
            elementDefinitionService.availableUiElements,
            elementDefinitionService.availableBindingElements,
            elementDefinitionService.allAvailableElements,
        ]).subscribe(([uiElementDefs, bindingDefs, elementDefs]) => {
            this.uiElementDefs = uiElementDefs;
            this.bindingDefs = bindingDefs;
            this.elementDefs = elementDefs;
        });
        this.stopInsertingNode$.subscribe(() => {
            this.insertingNode = undefined;
            this.notificationService.dismiss();
            setTimeout(() => {
                this.insertModeChange.emit(false);
            });
        });
    }
    ngOnChanges(changes) {
        if (changes.rootNode) {
            this.setRootNode(this.rootNode);
        }
        if (changes.settings) {
            switch (this.settings.displayType) {
                case DashboardDisplayType.Fixed:
                    this.sizeLabel = `${this.settings.width} x ${this.settings.height}`;
                    break;
                case DashboardDisplayType.Mobile:
                    this.sizeLabel = `mobile - ${this.settings.width} x ${this.settings.height}`;
                    break;
                default:
                    this.sizeLabel = 'autosize';
            }
            if (this.settings.newTilePosition === DashboardNewTilePosition.Auto && this.insertingNode) {
                if (!changes.rootNode) {
                    this.addElement(DashboardStructureService.getContainedUiElementNode(this.insertingNode).element);
                }
                this.cancelInserting();
            }
        }
        if (changes.editMode && !this.editMode) {
            this.cancelInserting();
        }
    }
    addBinding(node, element, property) {
        this.managementService.addBinding(node.child ?? DashboardStructureService.getContainedUiElementNode(node), element, property);
    }
    addTileBinding(node, element, property) {
        this.managementService.addBinding(node, element, property);
    }
    addElement(element) {
        return this.managementService.addTopLevelElement(element);
    }
    addGridNode(node) {
        this.structureService.addToRoot(node);
    }
    selectAllNodes() {
        this.grid?.selectAllNodes();
    }
    clearNodesSelection() {
        this.grid?.clearNodesSelection();
    }
    removeSelectedNodes() {
        this.grid?.removeSelectedNodes();
    }
    duplicateSelectedNodes() {
        this.grid?.duplicateSelectedNodes();
    }
    onGridNodesChange(data) {
        this.gridNodesChange.emit(data);
    }
    onModifyElement(changeEvent) {
        this.structureService.modifyElement(changeEvent.node, changeEvent.change);
    }
    onSettingClicked(node) {
        this.cancelInserting();
        const elementHeader = node.element?.properties?.header || 'element';
        const uiElementNode = DashboardStructureService.getContainedUiElementNode(node);
        const isBeta = dashboardElements.getDescription(uiElementNode.element?.tag)?.documentation.beta;
        const dialogConfig = {
            data: {
                title: `Configure ${elementHeader}`,
                titleBadge: isBeta
                    ? {
                        type: 'info',
                        text: 'Beta',
                    }
                    : undefined,
                component: ConfigureElementDialogComponent,
                componentData: {
                    element: cloneDeep(uiElementNode.element),
                    gridElement: cloneDeep(node.element),
                    bindings: cloneDeep(this.managementService.getBindings(uiElementNode)),
                    gridBindings: cloneDeep(this.managementService.getBindings(node)),
                    bindingDefs: this.bindingDefs,
                    generalPalette: this.settings.generalPalette ?? DEFAULT_PALLETE,
                },
                showCancel: true,
                showSave: true,
            },
            maxWidth: '100vw',
            backdropClass: 'dashboard-builder-dialog-backdrop',
        };
        const dialogRef = this.dialog.open(ModalDialogComponent, dialogConfig);
        dialogRef.afterClosed().subscribe((result) => {
            if (result.button === ModalDialogComponentButton.Save) {
                this.structureService.modifyElement(node, result.data.gridElement);
                this.structureService.modifyElement(uiElementNode, result.data.element);
                this.grid.refreshNode(node);
                this.managementService.setBindings(node, result.data.gridBindings);
                this.managementService.setBindings(uiElementNode, result.data.bindings);
                this.elementIssues = this.elementIssues.filter((error) => error.affected !== node);
                this.notificationService.dismiss();
            }
        });
    }
    onDuplicateElement({ node, forceAutoPlace }) {
        const nodeCopy = merge({}, node, {
            element: {
                properties: {
                    header: `Copy - ${node.element.properties.header}`,
                },
            },
        });
        if (forceAutoPlace) {
            this.addGridNode(nodeCopy);
        }
        else {
            this.setInsertingNode(nodeCopy);
        }
    }
    onRemoveElement(node) {
        this.structureService.removeNode(node);
        if (this.latestError?.affected === node ||
            this.latestError?.affected === DashboardStructureService.getContainedUiElementNode(node)) {
            this.notificationService.dismiss();
        }
    }
    onElementIssueClicked(node) {
        const uiElementNode = DashboardStructureService.getContainedUiElementNode(node);
        const errorsAndWarnings = this.elementIssues.filter((issue) => issue.affected === node || issue.affected === uiElementNode);
        const errors = errorsAndWarnings.filter((issue) => issue.severity === 'error');
        const dialogConfig = {
            data: {
                title: 'Issues',
                component: ElementIssueDialogComponent,
                componentData: {
                    // if errors are present, warnings aren't relevant
                    unresolvedIssues: errors.length !== 0 ? errors : errorsAndWarnings,
                },
                showClose: true,
                actionItem: errors.length === 0
                    ? { title: 'Warning', fill: '#F5C413', icon: 'alert/warning' }
                    : { title: 'Error', fill: 'red', icon: 'alert/error' },
            },
            backdropClass: 'dashboard-builder-dialog-backdrop',
        };
        this.dialog.open(ModalDialogComponent, dialogConfig);
    }
    onElementIssue(issue) {
        if (issue.resolved) {
            this.elementIssues = this.elementIssues.filter((elementError) => elementError.message !== issue.message || elementError.affected !== issue.affected);
            if (issue.affected === this.latestError?.affected && issue.message === this.latestError?.message) {
                this.notificationService.dismiss();
            }
        }
        else {
            const index = this.elementIssues.findIndex((ei) => ei.message === issue.message && ei.affected === issue.affected);
            if (index >= 0) {
                this.elementIssues = [...this.elementIssues.slice(0, index), issue, ...this.elementIssues.slice(index + 1)];
            }
            else {
                this.elementIssues = this.elementIssues.concat(issue);
            }
            if (issue.severity === 'error' && issue.message !== this.latestError?.message) {
                this.latestError = issue;
                this.notificationService.showError('Data Binding Error', issue.message);
            }
        }
    }
    onInsertElement({ item, keepOn }) {
        if (!this.insertingNode) {
            return;
        }
        this.structureService.addToRoot(merge({}, this.insertingNode, {
            element: {
                properties: {
                    x: item.x,
                    y: item.y,
                    cols: item.cols,
                    rows: item.rows,
                },
            },
        }));
        if (!keepOn) {
            this.cancelInserting();
        }
    }
    openAddElementDialog() {
        this.cancelInserting();
        const dialogConfig = {
            data: {
                title: 'Add element',
                component: AddElementDialogComponent,
                componentData: {
                    elementDefs: this.uiElementDefs,
                    generalPalette: this.settings.generalPalette ?? DEFAULT_PALLETE,
                },
                showClose: true,
                showCloseOnAdd: true,
            },
            backdropClass: 'dashboard-builder-dialog-backdrop',
        };
        const dialogRef = this.dialog.open(ModalDialogComponent, dialogConfig);
        dialogRef.afterClosed().subscribe((result) => {
            if (result.button === ModalDialogComponentButton.Close && result.data?.addedElements) {
                if (result.data.addedElements.length === 1) {
                    this.setInsertingNode(this.managementService.createTopLevelElement(this.createNewElement(result.data.addedElements[0])));
                }
                else {
                    result.data.addedElements.forEach((addedElement) => {
                        this.addElement(this.createNewElement(addedElement));
                    });
                }
            }
        });
    }
    cancelInserting() {
        this.stopInsertingNode$.next();
    }
    createNewElement(element) {
        const properties = {};
        element.properties.forEach((prop) => {
            if (prop.defaultValue) {
                properties[prop.name] = prop.defaultValue;
            }
        });
        return {
            tag: element.tag,
            properties,
        };
    }
    setInsertingNode(node) {
        if (this.settings.newTilePosition === DashboardNewTilePosition.Auto || this.grid.gridsterEl.mobile) {
            this.addGridNode(node);
            return;
        }
        this.insertingNode = node;
        this.notificationService.showInfo('Insert new element', 'Hold Shift to keep adding multiple instances. Press Esc to cancel.');
        fromEvent(window, 'keydown')
            .pipe(takeUntil(this.stopInsertingNode$))
            .subscribe((ev) => {
            if (ev.code === 'Escape') {
                this.cancelInserting();
            }
        });
        this.insertModeChange.emit(true);
    }
    setRootNode(node) {
        this.structureService.normalizeRootNode(node);
        this.structureService.setRootNode(node);
    }
}
DefaultDashboardComponent.DEFAULT_SETTINGS = createDefaultDashboardSettings();
DefaultDashboardComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: DefaultDashboardComponent, deps: [{ token: i1.DashboardStructureService }, { token: i1.DashboardManagementService }, { token: i2.MatDialog }, { token: i3.ElementDefinitionService }, { token: i3.NotificationService }], target: i0.ɵɵFactoryTarget.Component });
DefaultDashboardComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.0.0", type: DefaultDashboardComponent, selector: "dashboard-builder-default-dashboard", inputs: { rootNode: "rootNode", settings: "settings", editMode: "editMode", multiselect: "multiselect" }, outputs: { rootNodeChange: "rootNodeChange", insertModeChange: "insertModeChange", gridNodesChange: "gridNodesChange" }, providers: [DashboardStructureService, DashboardManagementService], viewQueries: [{ propertyName: "grid", first: true, predicate: DashboardGridComponent, descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div class=\"grid-outer\" [class.editmode]=\"editMode\">\n  <div class=\"size\" *ngIf=\"editMode\" title=\"See project settings to change size\">\n    {{ sizeLabel }}\n  </div>\n  <div\n    class=\"grid-wrapper\"\n    [ngClass]=\"{\n      'displaytype-fixed': settings.displayType === displayType.fixed,\n      'displaytype-mobile': settings.displayType === displayType.mobile,\n      'displaytype-auto': settings.displayType === displayType.auto,\n      'displaytype-noeditmode': !editMode\n    }\"\n    [ngStyle]=\"\n      settings.displayType !== displayType.auto && editMode\n        ? { height: settings.height + 'px', width: settings.width + 'px' }\n        : { width: 'unset' }\n    \"\n  >\n    <ng-container *ngIf=\"rootNode !== undefined && settings !== undefined; else missingDashboardData\">\n      <dashboard-builder-dashboard-grid\n        #grid\n        [root]=\"rootNode\"\n        [elementIssues]=\"elementIssues\"\n        [settings]=\"settings\"\n        [editMode]=\"editMode\"\n        [insertMode]=\"!!insertingNode\"\n        [multiselect]=\"multiselect\"\n        (settingClicked)=\"onSettingClicked($event)\"\n        (modifyElement)=\"onModifyElement($event)\"\n        (duplicateElement)=\"onDuplicateElement($event)\"\n        (removeElement)=\"onRemoveElement($event)\"\n        (elementWarningClicked)=\"onElementIssueClicked($event)\"\n        (elementErrorClicked)=\"onElementIssueClicked($event)\"\n        (elementIssue)=\"onElementIssue($event)\"\n        (insertElement)=\"onInsertElement($event)\"\n        (gridNodesChange)=\"onGridNodesChange($event)\"\n      ></dashboard-builder-dashboard-grid>\n    </ng-container>\n    <ng-template #missingDashboardData>\n      <davinci-callout type=\"warning\"> No dashboard data or settings available! </davinci-callout>\n    </ng-template>\n  </div>\n</div>\n", styles: [":host{width:100%;overflow:hidden}.grid-outer{width:100%;height:100%}.grid-outer.editmode{overflow:auto}.grid-outer .size{position:sticky;top:calc(100% - 30px);left:0;width:calc(100vw - 30px);height:0;text-align:right;white-space:nowrap;z-index:999;-webkit-user-select:none;user-select:none}.grid-wrapper{position:relative;height:100%;margin:auto;border:1px solid transparent;box-sizing:border-box}.grid-wrapper.displaytype-fixed{transition:.3s}.grid-wrapper.displaytype-mobile{transition:.3s;margin:auto}.grid-wrapper.displaytype-auto{transition:.3s;margin:unset}.grid-wrapper.displaytype-noeditmode{height:100%;transition:.3s;margin:unset}\n"], dependencies: [{ kind: "directive", type: i4.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i4.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i4.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: i5.DashboardGridComponent, selector: "dashboard-builder-dashboard-grid", inputs: ["root", "settings", "editMode", "multiselect", "elementIssues", "insertMode"], outputs: ["settingClicked", "modifyElement", "duplicateElement", "removeElement", "elementWarningClicked", "elementErrorClicked", "elementIssue", "insertElement", "gridNodesChange"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: DefaultDashboardComponent, decorators: [{
            type: Component,
            args: [{ selector: 'dashboard-builder-default-dashboard', providers: [DashboardStructureService, DashboardManagementService], template: "<div class=\"grid-outer\" [class.editmode]=\"editMode\">\n  <div class=\"size\" *ngIf=\"editMode\" title=\"See project settings to change size\">\n    {{ sizeLabel }}\n  </div>\n  <div\n    class=\"grid-wrapper\"\n    [ngClass]=\"{\n      'displaytype-fixed': settings.displayType === displayType.fixed,\n      'displaytype-mobile': settings.displayType === displayType.mobile,\n      'displaytype-auto': settings.displayType === displayType.auto,\n      'displaytype-noeditmode': !editMode\n    }\"\n    [ngStyle]=\"\n      settings.displayType !== displayType.auto && editMode\n        ? { height: settings.height + 'px', width: settings.width + 'px' }\n        : { width: 'unset' }\n    \"\n  >\n    <ng-container *ngIf=\"rootNode !== undefined && settings !== undefined; else missingDashboardData\">\n      <dashboard-builder-dashboard-grid\n        #grid\n        [root]=\"rootNode\"\n        [elementIssues]=\"elementIssues\"\n        [settings]=\"settings\"\n        [editMode]=\"editMode\"\n        [insertMode]=\"!!insertingNode\"\n        [multiselect]=\"multiselect\"\n        (settingClicked)=\"onSettingClicked($event)\"\n        (modifyElement)=\"onModifyElement($event)\"\n        (duplicateElement)=\"onDuplicateElement($event)\"\n        (removeElement)=\"onRemoveElement($event)\"\n        (elementWarningClicked)=\"onElementIssueClicked($event)\"\n        (elementErrorClicked)=\"onElementIssueClicked($event)\"\n        (elementIssue)=\"onElementIssue($event)\"\n        (insertElement)=\"onInsertElement($event)\"\n        (gridNodesChange)=\"onGridNodesChange($event)\"\n      ></dashboard-builder-dashboard-grid>\n    </ng-container>\n    <ng-template #missingDashboardData>\n      <davinci-callout type=\"warning\"> No dashboard data or settings available! </davinci-callout>\n    </ng-template>\n  </div>\n</div>\n", styles: [":host{width:100%;overflow:hidden}.grid-outer{width:100%;height:100%}.grid-outer.editmode{overflow:auto}.grid-outer .size{position:sticky;top:calc(100% - 30px);left:0;width:calc(100vw - 30px);height:0;text-align:right;white-space:nowrap;z-index:999;-webkit-user-select:none;user-select:none}.grid-wrapper{position:relative;height:100%;margin:auto;border:1px solid transparent;box-sizing:border-box}.grid-wrapper.displaytype-fixed{transition:.3s}.grid-wrapper.displaytype-mobile{transition:.3s;margin:auto}.grid-wrapper.displaytype-auto{transition:.3s;margin:unset}.grid-wrapper.displaytype-noeditmode{height:100%;transition:.3s;margin:unset}\n"] }]
        }], ctorParameters: function () { return [{ type: i1.DashboardStructureService }, { type: i1.DashboardManagementService }, { type: i2.MatDialog }, { type: i3.ElementDefinitionService }, { type: i3.NotificationService }]; }, propDecorators: { grid: [{
                type: ViewChild,
                args: [DashboardGridComponent]
            }], rootNode: [{
                type: Input
            }], settings: [{
                type: Input
            }], editMode: [{
                type: Input
            }], multiselect: [{
                type: Input
            }], rootNodeChange: [{
                type: Output
            }], insertModeChange: [{
                type: Output
            }], gridNodesChange: [{
                type: Output
            }] } });

class DashboardBuilderDefaultDashboardModule {
}
DashboardBuilderDefaultDashboardModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: DashboardBuilderDefaultDashboardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DashboardBuilderDefaultDashboardModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.0.0", ngImport: i0, type: DashboardBuilderDefaultDashboardModule, declarations: [DefaultDashboardComponent, ModalDialogComponent], imports: [CommonModule, DashboardBuilderCoreModule, DashboardBuilderViewModule, MatDialogModule], exports: [DefaultDashboardComponent] });
DashboardBuilderDefaultDashboardModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: DashboardBuilderDefaultDashboardModule, imports: [CommonModule, DashboardBuilderCoreModule, DashboardBuilderViewModule, MatDialogModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: DashboardBuilderDefaultDashboardModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, DashboardBuilderCoreModule, DashboardBuilderViewModule, MatDialogModule],
                    declarations: [DefaultDashboardComponent, ModalDialogComponent],
                    exports: [DefaultDashboardComponent],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { DashboardBuilderDefaultDashboardModule, DefaultDashboardComponent, ModalDialogComponent, ModalDialogComponentButton };
